import {noop} from '@github-ui/noop'
import {ActionList, ActionMenu, Button, Checkbox, FormControl, Portal} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {useCallback, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'
import {commitLocalUpdate} from 'relay-runtime'

import type {BlockFromOrganizationDuration} from '../../mutations/__generated__/blockUserFromOrganizationMutation.graphql'
import {blockUserFromOrganization} from '../../mutations/block-user-from-organization-mutation'
import styles from './BlockUserFromOrgDialog.module.css'
import {
  getHideOptionLabel,
  type HIDE_OPTIONS,
  HideCommentActionItems,
  toReportedContentClassifier,
} from './HideCommentActions'

export type BlockUserFromOrgDialogProps = {
  organization: {login: string; id: string}
  contentId: string
  contentAuthor: {login: string; id: string}
  contentUrl: string
  /**
   * Callback function to handle the block action instead of Relay.
   * If provided, the footer will be rendered without relay, allowing components outside of a relay environment to
   * render this dialog
   */
  onBlock?: (
    duration: string,
    hideComment: boolean,
    hiddenReason: HIDE_OPTIONS | undefined,
    organizationLogin: string,
    notifyBlockedUser: boolean,
    userLogin: string,
  ) => void
  onClose: () => void
}

export const BLOCKING_DURATION_TO_READABLE_MAP: Record<string, string> = {
  ONE_DAY: 'For 1 day',
  THREE_DAYS: 'For 3 days',
  SEVEN_DAYS: 'For 7 days',
  THIRTY_DAYS: 'For 30 days',
  INDEFINITE: 'Until I unblock them',
}

export const BLOCKING_DURATION_TO_DAYS_MAP: Record<string, string> = {
  ONE_DAY: '1',
  THREE_DAYS: '3',
  SEVEN_DAYS: '7',
  THIRTY_DAYS: '30',
  INDEFINITE: '0', // Indefinite means no specific duration, so we use '0' to indicate no expiration
}

const INITIAL_SELECTED_BLOCKING_DURATION = 'INDEFINITE'

export const BlockUserFromOrgDialog = ({onBlock, onClose, ...props}: BlockUserFromOrgDialogProps) => {
  const [selectedBlockingDuration, setSelectedBlockingDuration] = useState<BlockFromOrganizationDuration>(
    INITIAL_SELECTED_BLOCKING_DURATION,
  )

  // Default to true for notifying the blocked user
  const [notifyBlockedUser, setNotifyBlockedUser] = useState<boolean>(true)
  const [hideComment, setHideComment] = useState<boolean>(false)
  const [commentHideOption, setCommentHideOption] = useState<HIDE_OPTIONS | undefined>(undefined)
  const userToBlock = props.contentAuthor.login

  return (
    <Portal>
      <Dialog
        title={`Block ${props.contentAuthor.login} from ${props.organization.login}`}
        renderFooter={() =>
          onBlock ? (
            <BlockUserFromOrgDialogFooterWithoutRelay
              onBlock={onBlock}
              onClose={onClose}
              {...props}
              notifyBlockedUser={notifyBlockedUser}
              hideComment={hideComment}
              duration={selectedBlockingDuration}
              hiddenReason={commentHideOption}
            />
          ) : (
            // If onBlock is not provided, we assume we're using Relay and need to use the Relay footer
            <BlockUserFromOrgDialogFooterWithRelay
              onClose={onClose}
              {...props}
              notifyBlockedUser={notifyBlockedUser}
              hideComment={hideComment}
              duration={selectedBlockingDuration}
              hiddenReason={commentHideOption}
            />
          )
        }
        width="xlarge"
        height="auto"
        onClose={onClose}
        className={styles.BlockUserDialog}
      >
        <div className={styles.BlockUserContainer}>
          <div className={styles.BlockUserFormRow}>
            <div>
              <span>
                Blocking a user prevents them from interacting with repositories of this organization, such as opening
                or commenting on pull requests or issues. Learn more about{' '}
                <a href="https://docs.github.com/communities/maintaining-your-safety-on-github/blocking-a-user-from-your-organization">
                  blocking a user.
                </a>
              </span>
            </div>
          </div>
          <div className={styles.BlockUserFormRow}>
            <ActionMenu>
              <ActionMenu.Button>
                Block a user: {BLOCKING_DURATION_TO_READABLE_MAP[selectedBlockingDuration]}
              </ActionMenu.Button>
              <ActionMenu.Overlay>
                <ActionList selectionVariant="single">
                  <ActionList.Group>
                    <ActionList.GroupHeading>Block options</ActionList.GroupHeading>
                    {Object.keys(BLOCKING_DURATION_TO_READABLE_MAP).map(option => (
                      <ActionList.Item
                        key={option}
                        onSelect={() => {
                          setSelectedBlockingDuration(option as BlockFromOrganizationDuration)
                        }}
                        selected={option === selectedBlockingDuration}
                      >
                        {BLOCKING_DURATION_TO_READABLE_MAP[option]}
                      </ActionList.Item>
                    ))}
                  </ActionList.Group>
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
          </div>
          <DialogDivider />
          <h4>More options</h4>
          <FormControl className={styles.BlockUserFormControl}>
            <Checkbox
              checked={notifyBlockedUser}
              onChange={() => setNotifyBlockedUser(!notifyBlockedUser)}
              aria-checked={notifyBlockedUser}
            />
            <FormControl.Label>Send user a notification and show activity in timeline</FormControl.Label>
            <FormControl.Caption>
              <span className={styles.BlockUserHelpText}>
                You can notify <b>{userToBlock}</b> as to why they&apos;re being blocked. A public timeline entry will
                show that this user was blocked. They will receive an email notification and a notice when they try to
                comment with the following message:
                <div className={styles.BlockUserIndentedContainer}>
                  <blockquote>
                    A maintainer of the @{props.organization.login} organization has blocked you because of{' '}
                    <a href={props.contentUrl}>this content</a>. For more information please see{' '}
                    <a href="https://docs.github.com/site-policy/github-terms/github-community-guidelines">
                      the community guidelines
                    </a>
                    .
                  </blockquote>
                </div>
              </span>
            </FormControl.Caption>
          </FormControl>
          <FormControl>
            <Checkbox checked={hideComment} onChange={() => setHideComment(!hideComment)} aria-checked={hideComment} />
            <FormControl.Label>Hide this user&apos;s comments</FormControl.Label>
            <FormControl.Caption>
              <span className={styles.BlockUserHelpText}>
                All of their comments in this organization{' '}
                <a href="https://docs.github.com/communities/moderating-comments-and-conversations/managing-disruptive-comments#hiding-a-comment">
                  will be hidden
                </a>{' '}
                to all users and will display a reason.
              </span>
            </FormControl.Caption>
          </FormControl>
          <div className={styles.BlockUserLeftPadding}>
            <ActionMenu>
              <ActionMenu.Button>
                {(commentHideOption && getHideOptionLabel(commentHideOption)) ?? 'Choose a reason'}
              </ActionMenu.Button>
              <ActionMenu.Overlay>
                <ActionList selectionVariant="single">
                  <HideCommentActionItems onSelect={setCommentHideOption} />
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
          </div>
        </div>
      </Dialog>
    </Portal>
  )

  // Add this in once we update the mutation to support the note
  /*
    <FormControl>
      <FormControl.Label>Add an optional note:</FormControl.Label>
      <TextInput sx={{width: '100%'}} />
      <FormControl.Caption>
        <Text sx={{color: 'fg.subtle', fontSize: 12}}>
          Please don't include any personal information such as legal names or email addresses. Maximum 100
          characters, markdown supported. This note will be visible to only you.
        </Text>
      </FormControl.Caption>
    </FormControl>
  */
}

const DialogDivider = () => {
  return <div className={styles.BlockUserDivider} />
}

type BlockUserFromOrgDialogFooterProps = {
  notifyBlockedUser: boolean
  hideComment: boolean
  duration: BlockFromOrganizationDuration
  hiddenReason: HIDE_OPTIONS | undefined
} & BlockUserFromOrgDialogProps

const BlockUserFromOrgDialogFooterWithRelay = ({
  organization,
  contentAuthor,
  contentId,
  notifyBlockedUser,
  hideComment,
  duration,
  hiddenReason,
  onClose,
}: BlockUserFromOrgDialogFooterProps) => {
  const environment = useRelayEnvironment()

  const onSubmit = useCallback(() => {
    blockUserFromOrganization({
      environment,
      input: {
        organizationId: organization.id,
        contentId,
        blockedUserId: contentAuthor.id,
        duration,
        hiddenReason: hideComment && hiddenReason ? toReportedContentClassifier(hiddenReason) : undefined,
        notifyBlockedUser,
      },
      onError: noop,
      onCompleted: () =>
        commitLocalUpdate(environment, store => {
          const contentObject = store.get(contentId)
          contentObject?.setValue(false, 'pendingUnblock')
          contentObject?.setValue(true, 'pendingBlock')
          if (hiddenReason) {
            contentObject?.setValue(hiddenReason, 'pendingMinimizeReason')
          }
        }),
    })

    onClose()
  }, [
    environment,
    organization.id,
    contentId,
    contentAuthor.id,
    duration,
    hideComment,
    hiddenReason,
    notifyBlockedUser,
    onClose,
  ])

  return (
    <BlockUserFromOrgDialogFooterComponent hideComment={hideComment} hiddenReason={hiddenReason} onSubmit={onSubmit} />
  )
}

const BlockUserFromOrgDialogFooterWithoutRelay = ({
  contentAuthor,
  duration,
  hideComment,
  hiddenReason,
  onBlock,
  onClose,
  organization,
  notifyBlockedUser,
}: BlockUserFromOrgDialogFooterProps) => {
  const onSubmit = () => {
    onBlock?.(
      BLOCKING_DURATION_TO_DAYS_MAP[duration] ?? '0',
      hideComment,
      hiddenReason,
      organization.login,
      notifyBlockedUser,
      contentAuthor.login,
    )
    onClose()
  }

  return (
    <BlockUserFromOrgDialogFooterComponent hideComment={hideComment} hiddenReason={hiddenReason} onSubmit={onSubmit} />
  )
}

interface BlockUserFromOrgDialogFooterComponentProps {
  hideComment: boolean
  hiddenReason: HIDE_OPTIONS | undefined
  onSubmit: () => void
}

const BlockUserFromOrgDialogFooterComponent = ({
  hideComment,
  hiddenReason,
  onSubmit,
}: BlockUserFromOrgDialogFooterComponentProps) => {
  return (
    <Dialog.Footer>
      <Button variant={'danger'} disabled={hideComment && !hiddenReason} onClick={onSubmit} className={styles.Button}>
        Block user from organization
      </Button>
    </Dialog.Footer>
  )
}
